import { Link } from "wouter";
import { MapPin, Heart } from "lucide-react";

const EXPLORE_LINKS = [
  { href: "/order",      label: "Order Food" },
  { href: "/shop-local", label: "Shop Local" },
  { href: "/deals",      label: "Deals" },
  // "What's On | Places" — the two questions. `/events` is the canonical,
  // SEO-indexed What's On URL, so the footer links that rather than /whats-on.
  { href: "/events",     label: "What's On" },
  { href: "/places",     label: "Places" },
  { href: "/businesses", label: "Business Directory" },
  { href: "/news",       label: "Local News" },
];

const COMMUNITY_LINKS = [
  { href: "/jobs",         label: "Get a Quote" },
  { href: "/marketplace",  label: "Marketplace" },
  { href: "/vacancies",    label: "Jobs & Vacancies" },
  { href: "/community",    label: "Community" },
  // "Help & Support" used to point at /help, which is a directory of external
  // crisis helplines and cannot take a support request. That label promised
  // something the site could not do — the Terms of Service even relied on it
  // for marketing opt-outs. Now the two are named for what they actually are.
  { href: "/support",      label: "Contact Support" },
  { href: "/help",         label: "Local Support & Helplines" },
  { href: "/signup",       label: "Join the Hub" },
  { href: "/competition",  label: "Win Cash — Free Entry", highlight: true },
];

const BUSINESS_LINKS = [
  { href: "/business",    label: "Business Portal" },
  { href: "/advertise",   label: "Advertise With Us", highlight: true },
  { href: "/signup",      label: "List Your Business Free" },
  { href: "/order",       label: "Register Your Shop" },
  { href: "/become-a-driver", label: "Become a Driver" },
  { href: "/free-website", label: "Free Website Apply" },
];

export function Footer() {
  return (
    <footer className="bg-[#0c1e33] text-white mt-16">
      <div className="max-w-screen-xl mx-auto px-4 pt-14 pb-10">

        {/* Top row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
                <MapPin size={16} className="text-amber-400" />
              </div>
              <span className="font-bold text-lg tracking-tight">Ayrshire Hub</span>
            </div>
            <p className="text-white/50 text-sm leading-relaxed mb-5">
              The digital town square for Ayrshire — connecting residents, businesses and community groups.
            </p>
            <div className="flex flex-col gap-3 text-xs text-white/40">
              <span>© {new Date().getFullYear()} Scotdev Ltd (SC887365)</span>
              <span>Ayrshire Hub is a trading name of Scotdev Ltd</span>
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-[11px] font-black text-white/40 uppercase tracking-widest mb-4">Explore</h4>
            <a href="/browse-local" className="block text-sm text-white/60 hover:text-white mb-3">Takeaways and shops by town</a>
            <div className="flex flex-col gap-2.5">
              {EXPLORE_LINKS.map(l => (
                <Link key={l.href} href={l.href} className="text-sm text-white/60 hover:text-white transition-colors">{l.label}</Link>
              ))}
            </div>
          </div>

          {/* Community */}
          <div>
            <h4 className="text-[11px] font-black text-white/40 uppercase tracking-widest mb-4">Community</h4>
            <div className="flex flex-col gap-2.5">
              {COMMUNITY_LINKS.map(l => (
                <Link key={l.href} href={l.href} className={`text-sm transition-colors ${l.highlight ? "text-amber-400 hover:text-amber-300 font-semibold" : "text-white/60 hover:text-white"}`}>{l.label}</Link>
              ))}
            </div>
          </div>

          {/* Business */}
          <div>
            <h4 className="text-[11px] font-black text-white/40 uppercase tracking-widest mb-4">Business</h4>
            <div className="flex flex-col gap-2.5">
              {BUSINESS_LINKS.map(l => (
                <Link key={l.href} href={l.href} className="text-sm text-white/60 hover:text-white transition-colors">{l.label}</Link>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-white/35 text-xs">
          <div className="flex items-center gap-1.5">
            Made with <Heart size={11} className="text-red-400 fill-red-400" /> in Scotland
          </div>
          <div className="flex items-center flex-wrap justify-center sm:justify-end gap-x-5 gap-y-2">
            <Link href="/privacy" className="hover:text-white/70 transition-colors">Privacy Policy</Link>
            <Link href="/terms" className="hover:text-white/70 transition-colors">Terms of Service</Link>
            <Link href="/cookie-policy" className="hover:text-white/70 transition-colors">Cookie Policy</Link>
            {/* On every page, next to the other legal links, because PECR
                requires withdrawing marketing consent to be as easy as giving
                it — and free. Lands on the support form with the opt-out
                category pre-selected; no account needed. */}
            <Link href="/support?topic=unsubscribe" className="hover:text-white/70 transition-colors">Unsubscribe from Marketing</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
